document.documentElement.classList.add('js');

let revealObserver;

function initReveals(root = document) {
  const elements = [];

  if (root.matches && root.matches('.reveal')) elements.push(root);
  if (root.querySelectorAll) elements.push(...root.querySelectorAll('.reveal'));

  // Shopify's theme editor hot-reloads individual sections after a setting
  // changes. Keep those sections visible while editing so the preview never
  // appears blank after selecting a product or changing copy.
  if (window.Shopify && Shopify.designMode) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  if (!('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
  }

  elements.forEach((el) => {
    if (!el.classList.contains('is-visible')) revealObserver.observe(el);
  });
}

function initProductPanel(root = document) {
  const panel = root.matches && root.matches('.product-panel')
    ? root
    : root.querySelector?.('.product-panel');

  if (!panel || panel.dataset.traceInitialized === 'true') return;
  panel.dataset.traceInitialized = 'true';

  const form = panel.querySelector('form');
  const select = panel.querySelector('select[name="id"]');
  const sectionId = panel.dataset.sectionId;
  const variantScript = sectionId ? document.getElementById(`ProductVariants-${sectionId}`) : null;
  let variants = [];
  try {
    variants = variantScript ? JSON.parse(variantScript.textContent) : [];
  } catch (error) {
    console.warn('PARADIASKOS: variant data could not be read.', error);
  }
  const radios = [...panel.querySelectorAll('input[type="radio"][name^="options"]')];
  const price = panel.querySelector('[id^="ProductPrice-"]');

  if (!form || !select || !radios.length) return;

  const currency = document.documentElement.dataset.currency || 'CAD';
  const moneyFormat = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
    style: 'currency',
    currency
  }).format(cents / 100);

  radios.forEach((radio) => radio.addEventListener('change', () => {
    const optionNames = [...new Set(radios.map((r) => r.name))];
    const chosen = optionNames.map((name) => panel.querySelector(`input[name="${CSS.escape(name)}"]:checked`)?.value);
    const variant = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
    if (!variant) return;

    select.value = variant.id;
    if (price) price.textContent = moneyFormat(variant.price);

    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'ADD TRACE™ TO CART' : 'SOLD OUT';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }));
}

function initMobileNavigation(root = document) {
  const toggle = root.querySelector?.('.menu-toggle');
  const menu = root.querySelector?.('.mobile-nav');
  if (!toggle || !menu || toggle.dataset.initialized) return;
  toggle.dataset.initialized = 'true';
  const close = () => { toggle.setAttribute('aria-expanded', 'false'); menu.hidden = true; document.body.classList.remove('menu-open'); };
  toggle.addEventListener('click', () => {
    const opening = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(opening));
    menu.hidden = !opening;
    document.body.classList.toggle('menu-open', opening);
  });
  menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
}

function initSupportAssistant(root = document) {
  const assistant = root.matches?.('[data-trace-assistant]') ? root : root.querySelector?.('[data-trace-assistant]');
  if (!assistant || assistant.dataset.initialized) return;
  assistant.dataset.initialized = 'true';
  const trigger = assistant.querySelector('.trace-assistant__trigger');
  const panel = assistant.querySelector('.trace-assistant__panel');
  const closeButton = assistant.querySelector('[data-assistant-close]');
  if (!trigger || !panel) return;
  const setOpen = (open) => { panel.hidden = !open; trigger.setAttribute('aria-expanded', String(open)); if (open) closeButton?.focus(); };
  trigger.addEventListener('click', () => setOpen(panel.hidden));
  closeButton?.addEventListener('click', () => { setOpen(false); trigger.focus(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !panel.hidden) setOpen(false); });
}

function initCookieConsent(root = document) {
  const banner = root.matches?.('[data-cookie-consent]') ? root : root.querySelector?.('[data-cookie-consent]');
  if (!banner || banner.dataset.initialized) return;
  banner.dataset.initialized = 'true';
  const preferencesPanel = banner.querySelector('[data-cookie-preferences]');
  const manage = banner.querySelector('[data-cookie-manage]');
  const accept = banner.querySelector('[data-cookie-accept]');
  const reject = banner.querySelector('[data-cookie-reject]');
  const save = banner.querySelector('[data-cookie-save]');
  const preferences = banner.querySelector('[data-consent-preferences]');
  const analytics = banner.querySelector('[data-consent-analytics]');
  const marketing = banner.querySelector('[data-consent-marketing]');
  const reopenButtons = document.querySelectorAll('[data-cookie-reopen]');

  const hide = () => { banner.hidden = true; };
  const submit = (choices) => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.setTrackingConsent) return hide();
    privacy.setTrackingConsent(choices, hide);
  };
  const ready = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.shouldShowBanner?.()) banner.hidden = false;
  };

  manage?.addEventListener('click', () => { preferencesPanel.hidden = false; manage.hidden = true; accept.hidden = true; reject.hidden = true; save.hidden = false; });
  accept?.addEventListener('click', () => submit({ preferences: true, analytics: true, marketing: true }));
  reject?.addEventListener('click', () => submit({ preferences: false, analytics: false, marketing: false }));
  save?.addEventListener('click', () => submit({ preferences: preferences.checked, analytics: analytics.checked, marketing: marketing.checked }));
  reopenButtons.forEach((button) => button.addEventListener('click', () => {
    const privacy = window.Shopify?.customerPrivacy;
    const current = privacy?.currentVisitorConsent?.() || {};
    preferences.checked = current.preferences === 'yes';
    analytics.checked = current.analytics === 'yes';
    marketing.checked = current.marketing === 'yes';
    preferencesPanel.hidden = false;
    manage.hidden = true;
    accept.hidden = true;
    reject.hidden = true;
    save.hidden = false;
    banner.hidden = false;
  }));

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) ready(); });
  }
}

function initTheme(root = document) {
  initReveals(root);
  initProductPanel(root);
  initMobileNavigation(root);
  initSupportAssistant(root);
  initCookieConsent(root);
}

initTheme(document);

// Shopify Theme Editor events: sections are replaced without a full page load.
document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
document.addEventListener('shopify:section:select', (event) => initTheme(event.target));
